import { Link } from "@readium/shared";

/**
 * Because this file is imported in the main chunk,
 * we must avoid importing any PDF.js code here so it can stay in its own chunk and be loaded lazily.
 * We must only import TYPES from PDF.js
 */
import type * as PDFjs from "pdfjs-dist/build/pdf";
import type { PDFWorker } from "pdfjs-dist/build/pdf";
import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";

import { pdfResource } from "@/helpers/constants";
import { WPSClient } from "@/wps/client";

import OfflineReading from "../../../services/offline/OfflineService";

import { setPDFjsDependencies } from "./dependencies";

import {
  LoaderConfig,
  ModuleLoader,
} from "../../../../modules/dom-utils/loaders/module";
import { preloadFetch } from "../../../../modules/shared-utils/fetch";

import { PDFjsPreload } from "../../../../modules/pdfjs/preload";

// Don't touch this without also modifying in the WPS
const CHUNK_SIZE = 65536 * 16;

function preloadRanges(
  worker: PDFWorker,
  url: string,
  size: number,
  signal?: AbortSignal
) {
  let workerReady = false;
  worker.promise.then(() => (workerReady = true));

  // the first request allows PDF.js to discover the file size
  preloadFetch(
    url,
    new Response("", {
      headers: {
        "Accept-Ranges": "bytes",
        "Content-Length": size.toString(),
        "Content-Type": "application/pdf",
      },
    }),
    signal
  );

  const ranges = (function* () {
    // first chunk
    yield `bytes=0-${CHUNK_SIZE - 1}`;
    // last chunk
    const last = Math.floor(size / CHUNK_SIZE) * CHUNK_SIZE;
    yield `bytes=${last}-${size - 1}`;
    // start iteration at second chunk, end before last chunk
    for (let start = CHUNK_SIZE; start < last; start += CHUNK_SIZE) {
      yield `bytes=${start}-${start + CHUNK_SIZE - 1}`;
    }
  })();

  function preloadRange() {
    if (signal?.aborted) return;
    const range = ranges.next();
    if (range.done) return;

    preloadFetch(
      new Request(url, { headers: { Range: range.value as string } }),
      (response) => {
        if (!workerReady) preloadRange();
        return response;
      },
      signal
    );
  }

  OfflineReading.worker.untilControlledBy().then(() => preloadRange());
}

export async function getDocument(
  pdfjsLib: typeof PDFjs,
  docLink: Link,
  baseUrl: string,
  worker: PDFWorker,
  signal?: AbortSignal
): Promise<PDFDocumentProxy> {
  // What is happening here?
  // Well, PDF.js disabled byte range reading if the total size of the PDF is less than or equal
  // to 2 * the range reading chunk size. Because we try to disallow full reading of the PDF in
  // the WPS, normally this would be prevented, however the WPS has an exception in place now for
  // when the PDF is too small, in which case it allows full reading for now. On our side, we need
  // to disable using HEAD to get the PDF headers (what's being done in fetch-mod.ts), so we add
  // "#small" to the end of the URL to disable intercepting and modifying the request in
  // fetch-mod.ts because it checks if the URL ends with "/publication.pdf", which it doesn't
  // if it's "/publication.pdf#small". Hopefully we'll come up with a smarter solution in the future.
  const sizeProp =
    docLink.properties?.otherProperties[
      "https://ns.demarque.dev/web-reader/pdf#size"
    ];
  let small = false;
  if (typeof sizeProp === "number") {
    if (sizeProp <= CHUNK_SIZE * 2) small = true;
  }

  const url = docLink.toURL(baseUrl) + (small ? "#small" : "");

  // TODO: await control by SW

  if (small) {
    // small file are fetched in one chunk
    OfflineReading.worker
      .untilControlledBy()
      .then(() => preloadFetch(url, undefined, signal));
  } else if (typeof sizeProp === "number") {
    preloadRanges(worker, url, sizeProp, signal);
  }

  await OfflineReading.worker.untilControlledBy();
  signal?.throwIfAborted();

  return pdfjsLib.getDocument({
    url,
    worker,
    rangeChunkSize: CHUNK_SIZE,
    withCredentials: true,
    useWorkerFetch: true,
    disableAutoFetch: true,
    disableStream: false,
    // providing the file length allows PDF.js
    // to avoid making a HEAD request to get the file length
    length: typeof sizeProp === "number" ? sizeProp : undefined,
  }).promise;
}

// because of top-level await, we must enforce load ordering of PDF.js ourself
// see: https://github.com/mozilla/pdf.js/issues/17228
// the ModuleLoader respect the order of the chained calls,
// but will use modulepreload so they can be fetched/compiled in parallel
export class PDFLoader {
  private loader: LoaderConfig<{}, typeof import("../PDFjsNavigator.vue")>;

  private readonly webWorker: Worker | null;
  private document: Promise<PDFDocumentProxy> | null = null;

  constructor(
    private readonly client: WPSClient,
    private readonly signal?: AbortSignal
  ) {
    signal?.addEventListener("abort", () => this.dispose(), { once: true });
    this.webWorker = signal?.aborted ? null : PDFjsPreload.worker.claim();

    this.loader = new ModuleLoader(signal)
      .load(
        "pdfLib",
        PDFjsPreload.library,
        ({ pdfLib }) =>
          (pdfLib.GlobalWorkerOptions.workerSrc = PDFjsPreload.worker.url)
      )
      // we start the PDFjs worker as soon as possible
      // not waiting on subsequent steps
      .set(
        "pdfWorker",
        ({ pdfLib }) =>
          new pdfLib.PDFWorker({
            // typing error from PDFjs
            port: this.webWorker as null,
          })
      )
      // we start loading the PDF document as soon as possible
      .set("document", ({ pdfLib, pdfWorker }) => {
        const promise = this.loadDocument(pdfLib, pdfWorker);

        // silence unhandled rejection warning
        // that promise will get consumed later
        promise.catch((_) => void 0);
        this.document = promise;
        return { promise };
      })
      .load("viewer", PDFjsPreload.viewer)
      .load(
        "navigator",
        PDFjsPreload.navigator,
        ({ pdfLib, viewer, pdfWorker, document }) =>
          setPDFjsDependencies(pdfLib, viewer, pdfWorker, document.promise)
      );
  }

  get(): Promise<typeof import("../PDFjsNavigator.vue")> {
    return this.loader.get();
  }

  // the ModuleLoader stops the sequence; only what it already created needs releasing
  private dispose(): void {
    this.webWorker?.terminate();
    this.document?.then((doc) => doc.destroy()).catch(() => void 0);
  }

  private async loadDocument(
    pdfLib: typeof PDFjs,
    worker: PDFWorker
  ): Promise<PDFDocumentProxy> {
    const session = await this.client.session();
    this.signal?.throwIfAborted();

    let docLink = session.content.resources?.find(
      (link) => link.type === pdfResource.name
    );

    if (!docLink) {
      const manifest = await this.client.manifest();
      docLink = manifest.readingOrder.findWithMediaType(pdfResource.name);
    }

    return getDocument(
      pdfLib,
      docLink,
      // TODO: re-use base url computation from wpsClient's overrideManifest
      `/_proxy/${session.path}/`,
      worker,
      this.signal
    );
  }
}
